import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { useApiFetch } from '../../shared/api'

export interface GroupDto {
  id: string
  name: string
  photoUrl?: string
  role: string
  /** Region / area label for the team, when the backend provides one. */
  region?: string
  memberCount: number
  hectares: number
  nextHuntAt?: string
  joinCode?: string
  /** True when this is an estate team — drives the estate tag on every team surface. */
  isEstate?: boolean
  /**
   * The team's hunt-border colour, hex, absent when the team has not chosen one.
   *
   * Authoritative for every hunt border on every surface. The per-ground colour is
   * not: the app's live map resolves a border as the group's colour or the domain
   * default and never paints the per-ground value, and the app has no UI that writes
   * that value — so a colour set on either platform was invisible on the other, in
   * both directions. This is the only field both platforms can agree on.
   */
  borderColor?: string
}

export interface MyGroupsResponse {
  groups: GroupDto[]
}

export function useMyGroups() {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'me', 'groups', uid],
    queryFn: () => call<MyGroupsResponse>('/clubhouse/me/groups'),
    enabled: !!uid,
    // This payload carries the caller's own role in each team, which
    // `groupToTeam` turns into `Team.canEdit` — the gate on the Edit map
    // button. A permission must not outlive the change that granted or
    // revoked it, so cached data here is only ever a first paint: it is always
    // revalidated, never served as the answer.
    //
    // `staleTime: 0` is what actually delivers that, and it is not
    // interchangeable with `refetchOnMount: 'always'`. On a page load this
    // query mounts while `uid` is still undefined — Firebase restores the
    // session asynchronously — so it is DISABLED at mount and the
    // refetch-on-mount path never runs. It enables later, when the uid
    // arrives, and that transition consults staleness instead. Any non-zero
    // window therefore serves a persisted role unchallenged, which is exactly
    // how a promoted member stayed locked out: first with the original
    // 5-minute window, then still within a 30s one.
    //
    // Persisting the query is still right — it paints the sidebar badges
    // instantly on a cold load (see `persistedKeys` in the app's query
    // client) — but the painted value has to be provisional.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}
